import { useState } from 'react'
import {
  Chip,
  Typography,
  Box,
  Divider,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  FormControl,
  Button,
  Checkbox,
  Menu
} from '@mui/material'
import dayjs from 'dayjs'
import { TablePagination, TablePaginationProps } from '@/@core/components/mui/TablePagination'
import { useRouteQuery, useTranslator, useNotification } from '@/hooks'
import Link from '@/components/Link'
import EligibilityRateConfirmDialog from '@/pages/cms/finance/transaction/EligibilityRateConfirmDialog'
import { ICompany, ITransactionsCompany, useChangeBulkEligibilityMutation, IBulkEligibilityPayload } from '@/@data'
import CustomAvatar from '@/@core/components/mui/Avatar'
import { getInitials } from '@/utils/getInitials'

interface TransactionTableListProps {
  data: ITransactionsCompany[]
  meta?: unknown
  refetch?: () => void
  columnVisibility: Record<string, boolean>
}

export const TransactionTableList = ({ data, meta, columnVisibility, refetch }: TransactionTableListProps) => {
  const { __, __n } = useTranslator()
  const { searchQuery } = useRouteQuery()
  const n = useNotification()
  const bulkEligibilityMutation = useChangeBulkEligibilityMutation()

  const PREDEFINED_RATES = [60, 70, 80]
  const [rateType, setRateType] = useState<'predefined' | 'custom'>('predefined')
  const [selectedRate, setSelectedRate] = useState<number>(60)
  const [customRate, setCustomRate] = useState<number | ''>('')

  // Checkbox states - đơn giản như AttendanceTable
  const [isAllChecked, setIsAllChecked] = useState<boolean>(false)
  const [excludedCompanies, setExcludedCompanies] = useState<Set<number>>(new Set())
  const [selectedCompanies, setSelectedCompanies] = useState<Set<number>>(new Set())

  // Calculate selected count
  const getSelectedCount = () => {
    if (isAllChecked) {
      const totalCount = (meta as any)?.total || 0

      return totalCount - excludedCompanies.size
    } else {

      return selectedCompanies.size
    }
  }

  // Handle check all functionality
  const handleCheckAll = (checked: boolean) => {
    if (checked) {
      setIsAllChecked(true)
      setExcludedCompanies(new Set())
      setSelectedCompanies(new Set())
    } else {
      setIsAllChecked(false)
      setExcludedCompanies(new Set())
      setSelectedCompanies(new Set())
    }
  }

  // Handle individual checkbox
  const handleIndividualCheck = (id: number, checked: boolean) => {
    if (isAllChecked) {
      // flow 1: check all mode - manage excluded companies
      const newExcludedCompanies = new Set(excludedCompanies)
      
      if (!checked) {
        newExcludedCompanies.add(id)
      } else {
        newExcludedCompanies.delete(id)
      }
      
      // If exclusions cover the whole dataset, exit select-all mode
      const totalCount = (meta as any)?.total || 0
      
      if (totalCount > 0 && newExcludedCompanies.size >= totalCount) {
        setIsAllChecked(false)
        setExcludedCompanies(new Set())
        setSelectedCompanies(new Set())
      } else {
        setExcludedCompanies(newExcludedCompanies)
      }
    } else {
      // flow 2: individual selection mode - manage selected companies
      const newSelectedCompanies = new Set(selectedCompanies)
      
      if (checked) {
        newSelectedCompanies.add(id)
      } else {
        newSelectedCompanies.delete(id)
      }
      
      setSelectedCompanies(newSelectedCompanies)
    }
  }

  const effectiveRate = rateType === 'predefined' ? selectedRate : Number(customRate || 0)

  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  const buildBulkEligibilityPayload = (): IBulkEligibilityPayload => {
    if (isAllChecked) {
      return {
        is_all: 1,
        company_ids: [],
        exclude_company_ids: Array.from(excludedCompanies),
        eligibility_rate: effectiveRate
      }
    } else {
      return {
        is_all: 0,
        company_ids: Array.from(selectedCompanies),
        exclude_company_ids: [],
        eligibility_rate: effectiveRate
      }
    }
  }

  const handleSubmit = async () => {
    const selectedCount = getSelectedCount()

    if (selectedCount === 0) {
      n.error(__('Please select companies'))

      return
    }

    const payload = buildBulkEligibilityPayload()

    bulkEligibilityMutation.mutate(
      { payload },
      {
        onSuccess: () => {
          n.success(__('Eligibility rate updated successfully'))
          setSelectedCompanies(new Set())
          setExcludedCompanies(new Set())
          setIsAllChecked(false)
          refetch && refetch()
        },
        onError: () => {
          n.error(__('Update eligibility rate failed'))
        }
      }
    )
  }

  const handleOpenConfirm = () => {
    const selectedCount = getSelectedCount()

    if (selectedCount === 0) {
      n.error(__('Please select companies'))

      return
    }

    setIsConfirmOpen(true)
  }

  const getAvatar = (params: Pick<ICompany, 'logo_file' | 'name'>) => {
    const { logo_file, name } = params

    if (logo_file) {
      return <CustomAvatar src={logo_file.tiny_url} skin='light' size={34} />
    } else {
      return (
        <CustomAvatar skin='light' size={34}>
          {getInitials(name as string)}
        </CustomAvatar>
      )
    }
  }

  const columnOrder = [
    'employer',
    'employee',
    'eligibility_rate',
    'requested_transaction',
    'total_ewa_disbursement',
    'expired_contract',
    'total_transaction_fee',
    'voucher',
    'status'
  ]

  const isVisible = (key: string) => {
    const isFirstOrLast = key === columnOrder[0] || key === columnOrder[columnOrder.length - 1]

    return isFirstOrLast || columnVisibility[key] !== false
  }

  const [selectionAnchorEl, setSelectionAnchorEl] = useState<null | HTMLElement>(null)
  const selectionOpen = Boolean(selectionAnchorEl)

  return (
    <>
      {getSelectedCount() > 0 && (
        <>
          <Divider />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px'
            }}
          >
            <Box>
              <div className='flex items-center gap-2'>
                  <div>
                    <Button
                      variant='outlined'
                      color='secondary'
                      endIcon={<i className='tabler-chevron-down text-gray-600' />}
                      onClick={e => setSelectionAnchorEl(e.currentTarget)}
                      sx={{
                        borderRadius: '20px',
                        borderColor: '#d0d0d0',
                        color: '#666',
                        textTransform: 'none',
                        '&:hover': { borderColor: '#b0b0b0' }
                      }}
                    >
                      <span className='text-sm font-medium'>
                        {getSelectedCount()} {__('Selected')}
                      </span>
                    </Button>

                    <Menu
                      anchorEl={selectionAnchorEl}
                      open={selectionOpen}
                      onClose={() => setSelectionAnchorEl(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      sx={{ mt: 1 }}
                    >
                      <MenuItem
                        onClick={() => {
                          handleCheckAll(true)
                          setSelectionAnchorEl(null)
                        }}
                        sx={{ fontSize: '14px' }}
                      >
                        {(meta as any)?.total || 0} {__('Select all')}
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleCheckAll(false)
                          setSelectionAnchorEl(null)
                        }}
                        sx={{ fontSize: '14px' }}
                      >
                        {__('Unselect all')}
                      </MenuItem>
                    </Menu>
                  </div>

                  <div>
                    <Box>
                      <Box className='flex items-center gap-2'>
                        <FormControl size='medium'>
                          {rateType !== 'custom' ? (
                            <Select
                              value={selectedRate}
                              onChange={e => {
                                const value = e.target.value as number | 'custom'

                                if (value === 'custom') {
                                  setRateType('custom')
                                } else {
                                  setRateType('predefined')
                                  setSelectedRate(value)
                                }
                              }}
                              displayEmpty
                              sx={{ minWidth: 110, height: 38, borderRadius: '20px' }}
                            >
                              {PREDEFINED_RATES.map(rate => (
                                <MenuItem key={rate} value={rate}>
                                  {rate} %
                                </MenuItem>
                              ))}
                              <MenuItem value={'custom'}>{__('Custom')}</MenuItem>
                            </Select>
                          ) : (
                            <TextField
                              autoFocus
                              value={customRate}
                              onChange={e => {
                                const val = Number(e.target.value)

                                if (Number.isNaN(val)) {
                                  setCustomRate('')
                                } else {
                                  const bounded = Math.max(0, Math.min(100, val))

                                  setCustomRate(bounded)
                                }
                              }}
                              size='small'
                              type='number'
                              placeholder='0'
                              className='rounded-lg w-[140px] h-[38px]'
                              sx={{
                                '& .MuiInputBase-root': { borderRadius: '20px' },
                                '& input[type=number]': {
                                  '-moz-appearance': 'textfield'
                                },
                                '& input[type=number]::-webkit-outer-spin-button': {
                                  '-webkit-appearance': 'none',
                                  margin: 0
                                },
                                '& input[type=number]::-webkit-inner-spin-button': {
                                  '-webkit-appearance': 'none',
                                  margin: 0
                                }
                              }}
                              slotProps={{
                                input: {
                                  endAdornment: (
                                    <InputAdornment position='end'>
                                      <Button
                                        variant='text'
                                        size='small'
                                        onClick={() => {
                                          setRateType('predefined')
                                          setCustomRate('')
                                        }}
                                        sx={{
                                          minWidth: 'auto',
                                          padding: '2px 4px',
                                          height: '24px',
                                          borderRadius: '12px'
                                        }}
                                      >
                                        <i className='tabler-arrow-left text-xs' />
                                      </Button>
                                      <Button
                                        variant='text'
                                        size='small'
                                        onClick={() => setCustomRate('')}
                                        sx={{
                                          minWidth: 'auto',
                                          padding: '2px 4px',
                                          height: '24px',
                                          borderRadius: '12px'
                                        }}
                                      >
                                        <i
                                          className='tabler-x text-xs'
                                          style={{
                                            color: '#666'
                                          }}
                                        />
                                      </Button>
                                    </InputAdornment>
                                  )
                                }
                              }}
                            />
                          )}
                        </FormControl>
                      </Box>
                    </Box>
                  </div>
                </div>
            </Box>

            <Box className='flex items-center gap-2'>
              <Button
                variant='contained'
                color='primary'
                onClick={handleOpenConfirm}
                startIcon={<i className='tabler-check' />}
                className='rounded-full'
              >
                {__('Submit')}
              </Button>
              <Button
                variant='tonal'
                color='secondary'
                onClick={() => {
                  setSelectedCompanies(new Set())
                  setExcludedCompanies(new Set())
                  setIsAllChecked(false)
                }}
                startIcon={<i className='tabler-x' />}
                className='rounded-full'
              >
                {__('Clear')}
              </Button>
            </Box>
          </Box>
        </>
      )}

      <Box sx={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '1px solid #e0e0e0',
            tableLayout: 'fixed'
          }}
        >
          <thead>
            <tr className='border-right-custom'>
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 500,
                  fontSize: '13px',
                  width: 60
                }}
              >
                <Checkbox
                  checked={isAllChecked}
                  indeterminate={!isAllChecked && selectedCompanies.size > 0}
                  onChange={e => handleCheckAll(e.target.checked)}
                />
              </th>
              {isVisible('employer') && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    width: 350
                  }}
                >
                  {__('Employer')}
                </th>
              )}
              {isVisible('employee') && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    width: 140
                  }}
                >
                  {__('Employee')}
                </th>
              )}
              {isVisible('eligibility_rate') && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    width: 150
                  }}
                >
                  {__('Eligibility Rate')}
                </th>
              )}
              {isVisible('requested_transaction') && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    width: 180
                  }}
                >
                  {__('Requested Transaction')}
                </th>
              )}
              {isVisible('total_ewa_disbursement') && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    width: 280
                  }}
                >
                  {__('Total EWA Disbursement')}
                </th>
              )}
              {isVisible('expired_contract') && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    width: 170
                  }}
                >
                  {__('Expired Contract')}
                </th>
              )}
              {isVisible('total_transaction_fee') && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    width: 250
                  }}
                >
                  {__('Total Transaction Fee')}
                </th>
              )}
              {isVisible('voucher') && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    width: 200
                  }}
                >
                  {__('Voucher')}
                </th>
              )}
              {isVisible('status') && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    width: 180
                  }}
                >
                  {__('Status')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {(data || []).length === 0 && (
              <tr>
                <td colSpan={10} style={{ padding: 16, textAlign: 'center' }}>
                  <Typography className='text-[14px] font-normal text-[#2F2B3D]'>{__('No Data')}</Typography>
                </td>
              </tr>
            )}
            {(data || []).map((row: any) => {
              const id = Number(row?.id)

              return (
                <tr key={id}>
                  <td style={{ padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
                    <Checkbox
                      size='small'
                      checked={
                        isAllChecked
                          ? !excludedCompanies.has(id)
                          : selectedCompanies.has(id)
                      }
                      onChange={e => handleIndividualCheck(id, e.target.checked)}
                    />
                  </td>
                  {isVisible('employer') && (
                    <td style={{ padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
                      <Link href={`/cms/finance/transaction/${row?.id}?month=${row?.current_pay_period}`}>
                        <div className='flex items-center gap-4 no-underline'>
                          {getAvatar({ 
                            logo_file: row?.logo_file, 
                            name: row?.logo_file?.name || row.name || '-' 
                          })}
                          <div className='flex flex-col'>
                            <Typography
                              className='font-medium max-w-[200px] whitespace-nowrap overflow-hidden text-ellipsis text-bold'
                              color='text.primary'
                            >
                              {row?.name || '-'}
                            </Typography>
                            <Typography
                              className='font-medium max-w-[200px] whitespace-nowrap overflow-hidden text-ellipsis text-bold'
                              color='text.primary'
                            >
                              {row?.company_code || '-'}
                            </Typography>
                          </div>
                        </div>
                      </Link>
                    </td>
                  )}
                  {isVisible('employee') && (
                    <td style={{ padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
                      <div className='flex items-center gap-2'>
                        <i className='tabler-users text-[#3551F2]' />
                        <Typography className='text-[15px] leading-[22px] font-normal'>{row?.total_user}</Typography>
                      </div>
                    </td>
                  )}
                  {isVisible('eligibility_rate') && (
                    <td style={{ padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
                      <div className='flex items-center gap-2'>
                        <Typography className='text-[13px] text-[#fff] leading-[20px] font-normal bg-[#FF9F43] px-4 py-1 rounded-4xl'>
                          {__n(row?.eligibility_rate)} %
                        </Typography>
                      </div>
                    </td>
                  )}
                  {isVisible('requested_transaction') && (
                    <td style={{ padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
                      <Typography variant='body2' color='text.primary'>
                        {row?.request_num ?? 0}
                      </Typography>
                    </td>
                  )}
                  {isVisible('total_ewa_disbursement') && (
                    <td style={{ padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
                      <Typography variant='body2' color='text.primary'>
                        {__n(row?.request_amount)}
                      </Typography>
                    </td>
                  )}
                  {isVisible('expired_contract') && (
                    <td style={{ padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
                      <Typography variant='body2' color='text.primary'>
                        {row?.contract?.end_date ? dayjs(row?.contract?.end_date).format('DD/MM/YYYY') : '-'}
                      </Typography>
                    </td>
                  )}
                  {isVisible('total_transaction_fee') && (
                    <td style={{ padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
                      <Typography variant='body2' color='text.primary'>
                        {__n(row?.service_fee)}
                      </Typography>
                    </td>
                  )}
                  {isVisible('voucher') && (
                    <td style={{ padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
                      <Typography variant='body2' color='text.primary'>
                        {__n(row?.voucher_discount)}
                      </Typography>
                    </td>
                  )}
                  {isVisible('status') && (
                    <td style={{ padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
                      {(() => {
                        const employee: any = row

                        return (
                          <Chip
                            label={
                              employee.status ? employee.status.charAt(0).toUpperCase() + employee.status.slice(1) : ''
                            }
                            classes={
                              employee.status === 'active'
                                ? {
                                    root: 'bg-[#28C76F29] text-white rounded-[12px] px-2 py-2',
                                    label: 'text-[#28C76F]'
                                  }
                                : employee.status === 'expired'
                                  ? {
                                      root: 'bg-[#FF4C5129] text-[#FF4C51] rounded-[12px] px-4 py-2',
                                      label: 'text-[#FF4C51]'
                                    }
                                  : employee.status === 'disable'
                                    ? {
                                        root: 'bg-gray-100 text-[#6B7280] rounded-[12px] px-4 py-2',
                                        label: 'text-[#6B7280]'
                                      }
                                    : {
                                        root: 'bg-gray-50 text-gray-600 border-gray-200 rounded-[12px] px-4 py-2',
                                        label: 'text-gray-600'
                                      }
                            }
                          />
                        )
                      })()}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </Box>

      {meta && (
        <TablePagination
          meta={
            {
              ...meta,
              page: Number(searchQuery.get('page')) || 1
            } as TablePaginationProps
          }
        />
      )}

      <EligibilityRateConfirmDialog
        open={isConfirmOpen}
        effectiveRate={effectiveRate}
        selectedCompanyIds={isAllChecked ? [] : Array.from(selectedCompanies)}
        onCancel={() => {
          setIsConfirmOpen(false)
        }}
        onApply={option => {
          setIsConfirmOpen(false)
        
          if (option === 'new_only' || option === 'all_employees') {
            handleSubmit()
          }
        
          setSelectedCompanies(new Set())
          setExcludedCompanies(new Set())
          setIsAllChecked(false)
          refetch && refetch()
        }}
      />
    </>
  )
}

export default TransactionTableList
